import { useEffect, useState } from 'react';
import { Mail, ArrowUpRight, Instagram, Facebook, Youtube } from 'lucide-react';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { PROJECT_TYPES, BUDGET_RANGES, CONTACT, SOCIAL } from '../data/content';
import { Reveal, Eyebrow } from './Reveal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = { name: '', email: '', phone: '', project_type: '', budget: '', message: '' };

export const Contact = () => {
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const preset = (e) => setForm((f) => ({ ...f, project_type: e.detail }));
    window.addEventListener('invicto:preset', preset);
    return () => window.removeEventListener('invicto:preset', preset);
  }, []);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/inquiries`, {
        ...form,
        project_type: form.project_type || 'General Inquiry',
      });
      toast.success('Thank you — your inquiry is in. We will get back to you within one business day.');
      setForm(EMPTY);
    } catch {
      toast.error('Something went wrong sending your inquiry. Please try again in a moment.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="mx-auto max-w-7xl px-5 py-24 md:px-10 md:py-32">
      <div className="grid gap-14 lg:grid-cols-2 lg:gap-20">
        <div>
          <Eyebrow>Contact</Eyebrow>
          <Reveal>
            <h2 className="mt-4 font-serif text-3xl font-light leading-tight text-espresso sm:text-4xl lg:text-5xl">
              Tell us what you<br />
              have <span className="italic text-bronze">in mind.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 max-w-md text-sm font-light leading-relaxed text-stone md:text-base">
              A new custom home, a full-scale renovation or a single transformative remodel — send us
              the outline and we will come back with a plan, a timeline and a straight answer.
            </p>
          </Reveal>
          <Reveal delay={0.18} className="mt-10">
            <a href={`mailto:${CONTACT.email}`} data-testid="contact-direct-email" className="group inline-flex items-center gap-4 text-espresso transition-colors hover:text-bronze">
              <span className="flex h-11 w-11 items-center justify-center border border-gold bg-gold/10 text-bronze transition-colors group-hover:bg-gold group-hover:text-espresso">
                <Mail size={16} />
              </span>
              <span className="text-sm md:text-base">{CONTACT.email}</span>
            </a>
            <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.2em] text-stone">
              We reply within one business day
            </p>
            <div className="mt-5 flex gap-3">
              {SOCIAL.map((s) => (
                <a
                  key={s.id}
                  href={s.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`contact-social-${s.id}`}
                  aria-label={`Invicto Homes on ${s.label}`}
                  className="flex h-10 w-10 items-center justify-center border border-bronze/30 text-bronze transition-all duration-300 hover:bg-gold hover:text-espresso"
                >
                  {s.id === 'instagram' && <Instagram size={16} />}
                  {s.id === 'facebook' && <Facebook size={16} />}
                  {s.id === 'youtube' && <Youtube size={16} />}
                </a>
              ))}
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.1}>
          <form
            onSubmit={submit}
            data-testid="contact-form"
            className="space-y-6 border border-gold/50 bg-sand p-6 shadow-warm-sm md:p-9"
          >
            <div className="grid gap-6 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="c-name" className="text-coffee">Full Name *</Label>
                <Input
                  id="c-name" required data-testid="contact-input-name" placeholder="Your name"
                  className="border-bronze/30 bg-white text-espresso placeholder:text-stone/60 focus-visible:ring-gold"
                  value={form.name} onChange={set('name')}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="c-email" className="text-coffee">Email *</Label>
                <Input
                  id="c-email" type="email" required data-testid="contact-input-email" placeholder="you@email.com"
                  className="border-bronze/30 bg-white text-espresso placeholder:text-stone/60 focus-visible:ring-gold"
                  value={form.email} onChange={set('email')}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="c-phone" className="text-coffee">Phone</Label>
                <Input
                  id="c-phone" data-testid="contact-input-phone" placeholder="(555) 000-0000"
                  className="border-bronze/30 bg-white text-espresso placeholder:text-stone/60 focus-visible:ring-gold"
                  value={form.phone} onChange={set('phone')}
                />
              </div>
              <div className="space-y-2">
                <Label className="text-coffee">Project Type *</Label>
                <Select value={form.project_type} onValueChange={(v) => setForm({ ...form, project_type: v })}>
                  <SelectTrigger data-testid="contact-select-project-type" className="w-full border-bronze/30 bg-white text-espresso focus:ring-gold">
                    <SelectValue placeholder="What are we building?" />
                  </SelectTrigger>
                  <SelectContent className="border-bronze/25 bg-paper text-espresso">
                    {PROJECT_TYPES.map((t) => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-coffee">Estimated Budget</Label>
              <Select value={form.budget} onValueChange={(v) => setForm({ ...form, budget: v })}>
                <SelectTrigger data-testid="contact-select-budget" className="w-full border-bronze/30 bg-white text-espresso focus:ring-gold">
                  <SelectValue placeholder="Optional — helps us plan the first call" />
                </SelectTrigger>
                <SelectContent className="border-bronze/25 bg-paper text-espresso">
                  {BUDGET_RANGES.map((b) => (
                    <SelectItem key={b} value={b}>{b}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="c-message" className="text-coffee">Your Project *</Label>
              <Textarea
                id="c-message" required rows={5} data-testid="contact-input-message"
                placeholder="Tell us about the home you want to build, the house you want to transform, or the remodel you have in mind…"
                className="border-bronze/30 bg-white text-espresso placeholder:text-stone/60 focus-visible:ring-gold"
                value={form.message} onChange={set('message')}
              />
            </div>

            <Button
              type="submit" disabled={submitting} data-testid="contact-submit-button"
              className="group flex w-full items-center justify-center gap-2 bg-gold py-4 font-mono text-[11px] uppercase tracking-[0.25em] text-espresso hover:bg-goldhover"
            >
              {submitting ? 'Sending…' : 'Send Inquiry'}
              {!submitting && <ArrowUpRight size={15} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />}
            </Button>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
